import React, { useEffect, useRef } from 'react';
import { App as AntdApp } from 'antd';
import { ADMIN_BUILD_INFO } from '../../.generated/build-info';
import { prepareReviewDraftsForReload } from '../pages/photo-compliance/reviewDraftStore';

const CHECK_INTERVAL_MS = 60_000;

type RemoteBuildInfo = {
  buildId?: unknown;
  build_id?: unknown;
};

function adminStaticBase(): string {
  return window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/') ? '/admin' : '';
}

function remoteBuildInfoUrl(): string {
  return `${adminStaticBase()}/admin-build.json?t=${Date.now()}`;
}

function remoteBuildId(payload: RemoteBuildInfo): string | null {
  const value = typeof payload.buildId === 'string' ? payload.buildId : payload.build_id;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

const BuildVersionGuard: React.FC = () => {
  const { modal } = AntdApp.useApp();
  const promptedRef = useRef(false);

  useEffect(() => {
    let disposed = false;

    async function checkBuildVersion() {
      if (promptedRef.current) {
        return;
      }

      try {
        const response = await fetch(remoteBuildInfoUrl(), {
          cache: 'no-store',
          headers: {
            'cache-control': 'no-cache',
          },
        });
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as RemoteBuildInfo;
        const latestBuildId = remoteBuildId(payload);
        if (disposed || !latestBuildId || latestBuildId === ADMIN_BUILD_INFO.buildId) {
          return;
        }

        promptedRef.current = true;
        const draftState = prepareReviewDraftsForReload();
        if (draftState.hasDraft && !draftState.verified) {
          modal.confirm({
            title: '电脑端已更新',
            content: '审核草稿暂未能安全保存。继续刷新可能丢失本次选择，请先留在当前页面重试保存。',
            okText: '仍要刷新',
            cancelText: '暂不刷新',
            okButtonProps: { danger: true },
            onOk: () => window.location.reload(),
            onCancel: () => {
              promptedRef.current = false;
            },
          });
          return;
        }
        modal.warning({
          title: '电脑端已更新',
          content: draftState.hasDraft
            ? '草稿已保存，更新后可继续。请刷新后使用最新电脑端。'
            : '当前页面仍在运行旧版本。为避免旧页面提交过期排班数据，请刷新后继续操作。',
          okText: '刷新页面',
          onOk: () => window.location.reload(),
        });
      } catch {
        // Version checking is a safety net. Network failure must not block work.
      }
    }

    void checkBuildVersion();
    const timer = window.setInterval(() => void checkBuildVersion(), CHECK_INTERVAL_MS);
    const onFocus = () => void checkBuildVersion();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkBuildVersion();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [modal]);

  return null;
};

export default BuildVersionGuard;
